import type {
  AiPromptBoxAttachments,
  AttachmentDropdownConfig,
  DesignAttachment,
  LoadingAttachment,
  PromptAttachment,
  UserImageAttachment,
  UserImageAttachmentType,
} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PendingUploadStatus, PromptAttachmentDivider, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getAssetUrl} from '@Utils/s3.util';
import type {AttachmentImageData} from '@Components/zoomable-lightbox/zoomable-lightbox.types';
import type {ImagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import {getAiReferenceAttachmentThumbUrl, getUserImageScreenBigUrl, getUserImageThumbUrl} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import type {ElementData, ImageData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import type {UserMediaVOResponse} from '@Libraries/user-media-library';
import {UserMediaType, userMediaVOToElementData} from '@Libraries/user-media-library';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {checkUserImagePresets, getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {getUserId} from '@Libraries/user.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {generatePendingLocalId} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';

export const MAX_ATTACHMENTS = 6;
export const LOADING_ATTACHMENT_CANCELLED_EVENT = 'pmw-loading-attachment-cancelled';

export const DEFAULT_ATTACHMENT_DROPDOWN: AttachmentDropdownConfig = [
  PromptAttachmentType.UPLOAD_FROM_DEVICE,
  PromptAttachmentType.CAPTURE_PHOTO,
  PromptAttachmentDivider.HORIZONTAL_LINE,
  PromptAttachmentType.IMAGE,
  PromptAttachmentType.DESIGN,
  PromptAttachmentType.DESIGN_TEMPLATE,
];

export const getDefaultAttachments = (items: PromptAttachment[] = []): AiPromptBoxAttachments => ({
  items,
  dropdown: DEFAULT_ATTACHMENT_DROPDOWN,
});

export const getAttachmentPreviewUrl = (attachment: PromptAttachment, useThumbSize = true): string => {
  switch (attachment.type) {
    case PromptAttachmentType.REFERENCE_IMAGE:
      if (attachment.pendingUpload) {
        return attachment.pendingUpload.blobUrl;
      }
      return getAiReferenceAttachmentThumbUrl(attachment.hashedFilename, attachment.extension);
    case PromptAttachmentType.IMAGE:
    case PromptAttachmentType.CAPTURE_PHOTO:
      if (attachment.pendingUpload) {
        return attachment.pendingUpload.blobUrl;
      }
      if (useThumbSize) {
        return getUserImageThumbUrl(attachment.hashedFilename, attachment.extension);
      }
      return getUserImageScreenBigUrl(attachment.hashedFilename);
    case PromptAttachmentType.DESIGN:
    case PromptAttachmentType.DESIGN_TEMPLATE:
      return attachment.previewURL;
    case PromptAttachmentType.EXAMPLE_IMAGE:
      return getAssetUrl(attachment.fileName);
    case PromptAttachmentType.REATTACHED_MODEL_IMAGE:
      return getUrlForGeneratedAiChatModelAssetsImage(attachment.imageHashedId, MediaItemSizeType.THUMB);
  }
};

export const getDesignAttachmentScreenURLKey = (designId: string, pageHashedId?: string): string => {
  return pageHashedId ? `${designId}-${pageHashedId}` : designId;
};

export const getAttachmentUniqueKey = (attachment: PromptAttachment): string => {
  switch (attachment.type) {
    case PromptAttachmentType.IMAGE:
    case PromptAttachmentType.CAPTURE_PHOTO:
    case PromptAttachmentType.REFERENCE_IMAGE:
      return `${attachment.type}-${attachment.hashedFilename || attachment.localId || ''}`;
    case PromptAttachmentType.DESIGN:
    case PromptAttachmentType.DESIGN_TEMPLATE:
      return `${attachment.type}-${attachment.designId}${attachment.pageHashedId ? `-${attachment.pageHashedId}` : ''}`;
    case PromptAttachmentType.EXAMPLE_IMAGE:
      return `${attachment.type}-${attachment.fileName}`;
    case PromptAttachmentType.REATTACHED_MODEL_IMAGE:
      return `${attachment.type}-${attachment.imageHashedId}`;
  }
};

export const isDesignAttachment = (attachment: PromptAttachment): attachment is DesignAttachment => {
  return attachment.type === PromptAttachmentType.DESIGN || attachment.type === PromptAttachmentType.DESIGN_TEMPLATE;
};

const getDesignScreenURLFromCache = (attachment: DesignAttachment, designScreenURLs?: Record<string, string>): string => {
  const cacheKey = getDesignAttachmentScreenURLKey(attachment.designId, attachment.pageHashedId);
  return designScreenURLs?.[cacheKey] ?? '';
};

export const convertPromptAttachmentToImageData = (
  attachment: PromptAttachment,
  useThumbSize = true,
  designScreenURLs?: Record<string, string>
): AttachmentImageData => {
  const src = !useThumbSize && isDesignAttachment(attachment)
    ? getDesignScreenURLFromCache(attachment, designScreenURLs)
    : getAttachmentPreviewUrl(attachment, useThumbSize);

  return {
    type: ChatMessagePartType.MODEL_GENERATED_IMAGE,
    src,
    alt: 'Attachment',
    uniqueKey: getAttachmentUniqueKey(attachment),
  };
};

export const convertPromptAttachmentsToImageData = (
  attachments: PromptAttachment[],
  useThumbSize = true,
  designScreenURLs?: Record<string, string>
): AttachmentImageData[] => {
  return attachments.map((attachment) => {
    return convertPromptAttachmentToImageData(attachment, useThumbSize, designScreenURLs);
  });
};

export const convertElementDataToAttachment = (item: ElementData, type: PromptAttachmentType.IMAGE | PromptAttachmentType.CAPTURE_PHOTO): UserImageAttachment => {
  if (item.type !== ElementDataType.IMAGE) {
    throw new Error('Only images can be added as attachments');
  }

  const imageData = item as ImageData;
  return {
    type: type,
    hashedFilename: imageData.hashedFilename,
    extension: imageData.extension,
    source: imageData.source,
    width: imageData.width,
    height: imageData.height,
  };
};

export const convertUserMediaVOToAttachment = (
  response: UserMediaVOResponse,
  type: PromptAttachmentType.IMAGE | PromptAttachmentType.CAPTURE_PHOTO = PromptAttachmentType.IMAGE
): UserImageAttachment => {
  const imageResponse = response as UserImageVOResponse;
  const elementData = userMediaVOToElementData(UserMediaType.IMAGE, imageResponse);
  return convertElementDataToAttachment(elementData, type);
};

export interface PendingImageAttachmentResult {
  attachment: UserImageAttachment;
  localId: string;
  blobUrl: string;
}

export const createPendingImageAttachment = (file: File, type: UserImageAttachmentType = PromptAttachmentType.IMAGE): PendingImageAttachmentResult => {
  const blobUrl = URL.createObjectURL(file);
  const localId = generatePendingLocalId();
  const extension = getExtensionFromFile(file);
  const attachment: UserImageAttachment = {
    type,
    hashedFilename: '',
    extension,
    source: '',
    localId,
    pendingUpload: {
      blobUrl,
      status: PendingUploadStatus.UPLOADING,
    },
  };
  return {attachment, localId, blobUrl};
};

const getExtensionFromFile = (file: File): string => {
  const dotIndex = file.name.lastIndexOf('.');
  if (dotIndex >= 0 && dotIndex < file.name.length - 1) {
    return file.name.substring(dotIndex + 1).toLowerCase();
  }
  return '';
};

export const isAttachmentDuplicate = (attachment: UserImageAttachment, existingAttachments: PromptAttachment[]): boolean => {
  return existingAttachments.some((item) => item.type === PromptAttachmentType.IMAGE && 'hashedFilename' in item && item.hashedFilename === attachment.hashedFilename);
};

export const generateLoadingId = (): string => {
  return `loading-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

export const getFileSignature = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`;
};

export const isFileDuplicateBySignature = (file: File, existingFiles: File[]): boolean => {
  const fileSignature = getFileSignature(file);
  return existingFiles.some((existingFile) => getFileSignature(existingFile) === fileSignature);
};

export const isReattachableImagePart = (part: ImagePart): boolean => {
  return (
    part.type === ChatMessagePartType.USER_IMAGE ||
    part.type === ChatMessagePartType.DESIGN ||
    part.type === ChatMessagePartType.EXAMPLE_IMAGE_ASSET ||
    part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE ||
    part.type === ChatMessagePartType.REATTACHED_MODEL_IMAGE
  );
};

export const convertImagePartToPromptAttachment = (part: ImagePart, previewUrl?: string): PromptAttachment | null => {
  switch (part.type) {
    case ChatMessagePartType.USER_IMAGE:
      return {type: PromptAttachmentType.IMAGE, hashedFilename: part.hashedFilename, extension: part.extension, source: part.source};
    case ChatMessagePartType.DESIGN:
      return {type: PromptAttachmentType.DESIGN, designId: part.hashedId, previewURL: previewUrl ?? part.previewUrl ?? '', pageHashedId: part.pageHashedId};
    case ChatMessagePartType.MODEL_GENERATED_IMAGE:
    case ChatMessagePartType.REATTACHED_MODEL_IMAGE:
      return {type: PromptAttachmentType.REATTACHED_MODEL_IMAGE, imageHashedId: part.hashedId};
    case ChatMessagePartType.EXAMPLE_IMAGE_ASSET:
      return {type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: part.fileName};
    case ChatMessagePartType.IMAGE_GRAPHIC_ITEM_SNAPSHOT:
    default:
      return null;
  }
};

export const validatePresetsInBackground = async (hashedIds: string[], removeImageAttachments: (hashedFilenames: string[]) => void): Promise<void> => {
  if (!getUserId() || hashedIds.length === 0) {
    return;
  }

  try {
    const validationResults = await checkUserImagePresets(hashedIds);
    const invalidHashedIds = hashedIds.filter((hashedId) => !validationResults[hashedId]);

    if (invalidHashedIds.length === 0) {
      return;
    }

    removeImageAttachments(invalidHashedIds);

    const errorKey = invalidHashedIds.length > 1 ? 'pmwjs_error_processing_some_images' : 'pmwjs_error_processing_some_image';
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t(errorKey),
      showCloseIcon: true,
      interval: 7000,
    });
  } catch {
    removeImageAttachments(hashedIds);
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_error_processing_all_images'),
      showCloseIcon: true,
      interval: 7000,
    });
  }
};

export const createLoadingAttachments = (count: number): {loadingIds: string[]; newLoadingAttachments: LoadingAttachment[]} => {
  const loadingIds = Array.from({length: count}, () => generateLoadingId());
  const newLoadingAttachments = loadingIds.map((id) => ({id}));
  return {loadingIds, newLoadingAttachments};
};

export class LoadingAttachmentsTracker {
  private readonly loadingIds: string[] = [];
  private readonly cancelledIds = new Set<string>();
  private isListening = false;

  addIds(ids: string[]): void {
    this.loadingIds.push(...ids);
    if (!this.isListening) {
      window.addEventListener(LOADING_ATTACHMENT_CANCELLED_EVENT, this.handleCancel);
      this.isListening = true;
    }
  }

  resolveIds(count: number): {idsToRemove: string[]; activeIds: string[]} {
    const idsToRemove = this.loadingIds.splice(0, count);
    const activeIds = idsToRemove.filter((id) => !this.cancelledIds.has(id));
    this.cleanupIfDone();
    return {idsToRemove, activeIds};
  }

  resolveAllOnError(): string[] {
    const allRemaining = this.loadingIds.splice(0);
    this.cleanup();
    return allRemaining;
  }

  getLoadingIds(): string[] {
    return this.loadingIds;
  }

  isCancelled(id: string): boolean {
    return this.cancelledIds.has(id);
  }

  isDone(): boolean {
    return this.loadingIds.length === 0;
  }

  cleanupIfDone(): void {
    if (this.loadingIds.length === 0) {
      this.cleanup();
    }
  }

  cleanup(): void {
    if (this.isListening) {
      window.removeEventListener(LOADING_ATTACHMENT_CANCELLED_EVENT, this.handleCancel);
      this.isListening = false;
    }
  }

  private readonly handleCancel = (e: Event): void => {
    const {id} = (e as CustomEvent<{id: string}>).detail;
    if (this.loadingIds.includes(id)) {
      this.cancelledIds.add(id);
    }
  };
}
